import React, { useState } from 'react';
import { Bell, BellOff, Volume2, VolumeX, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';

export default function NotificationSettings() {
  // Cookie helpers for persistent backup
  const setCookie = (name, value, days = 365) => {
    const d = new Date();
    d.setTime(d.getTime() + days * 86400000);
    document.cookie = `${name}=${value};expires=${d.toUTCString()};path=/;SameSite=Lax`;
  };
  const getCookie = (name) => {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? match[2] : null;
  };

  // Initialize state from localStorage with cookie fallback
  const getInitialNotificationState = () => {
    const saved = localStorage.getItem('notifications_enabled') || getCookie('notifications_enabled');
    return saved === 'true';
  };
  
  const getInitialSoundState = () => {
    const saved = localStorage.getItem('notification_sound_enabled') || getCookie('notification_sound_enabled');
    return saved !== 'false'; // Default to true
  };
  
  const getInitialPermission = () => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      return Notification.permission === 'granted';
    }
    return false;
  };

  const [notificationsEnabled, setNotificationsEnabled] = useState(getInitialNotificationState);
  const [soundEnabled, setSoundEnabled] = useState(getInitialSoundState);
  const [hasPermission, setHasPermission] = useState(getInitialPermission);
  const [isOpen, setIsOpen] = useState(false);

  // Refresh permission when popover opens - use callback form
  const handleOpenChange = (open) => {
    setIsOpen(open);
    if (open && 'Notification' in window) {
      const currentPermission = Notification.permission === 'granted';
      if (currentPermission !== hasPermission) {
        setHasPermission(currentPermission);
      }
    }
  };

  const requestPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('Your browser does not support notifications');
      return;
    }

    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      setHasPermission(true);
      setNotificationsEnabled(true);
      localStorage.setItem('notifications_enabled', 'true');
      setCookie('notifications_enabled', 'true');
      toast.success('Notifications enabled!');
    } else {
      toast.error('Notification permission denied');
    }
  };

  const toggleNotifications = (checked) => {
    if (checked && !hasPermission) {
      requestPermission();
    } else {
      setNotificationsEnabled(checked);
      localStorage.setItem('notifications_enabled', String(checked));
      setCookie('notifications_enabled', String(checked));
      toast.success(checked ? 'Notifications enabled' : 'Notifications disabled');
    }
  };

  const toggleSound = (checked) => {
    setSoundEnabled(checked);
    localStorage.setItem('notification_sound_enabled', String(checked));
    setCookie('notification_sound_enabled', String(checked));
    
    if (checked) {
      // Play test sound
      const audio = new Audio('/sounds/message.ogg');
      audio.volume = 0.5;
      audio.play().catch(() => {});
    }
    
    toast.success(checked ? 'Sound enabled' : 'Sound muted');
  };

  const testNotification = () => {
    // Play sound if enabled
    if (soundEnabled) {
      const audio = new Audio('/sounds/message.ogg');
      audio.volume = 0.7;
      audio.play().catch(() => {});
    }

    // Show notification if enabled
    if (hasPermission && notificationsEnabled) {
      new Notification('📬 New Email', {
        body: 'This is a test notification from MyPatriotMail',
        icon: '/mpm-logo.png',
        badge: '/mpm-favicon.png'
      });
    }

    toast.success('Test notification sent!');
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative text-gray-400 hover:text-white hover:bg-[#2d2d44]"
          data-testid="notification-settings-btn"
        >
          {notificationsEnabled && hasPermission ? (
            <Bell className="w-5 h-5" />
          ) : (
            <BellOff className="w-5 h-5" />
          )}
          {notificationsEnabled && hasPermission && (
            <span className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-80 bg-[#1a1a2e] border-[#2d2d44] p-0"
        align="end"
      >
        <div className="p-4 border-b border-[#2d2d44]">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Bell className="w-4 h-4 text-[#6D4AFF]" />
            Notification Settings
          </h3>
          <p className="text-xs text-gray-400 mt-1">
            Get notified when you receive new emails
          </p>
        </div>

        <div className="p-4 space-y-4">
          {/* Browser Notifications */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${notificationsEnabled && hasPermission ? 'bg-[#6D4AFF]/20' : 'bg-[#252540]'}`}>
                {notificationsEnabled && hasPermission ? (
                  <Bell className="w-4 h-4 text-[#6D4AFF]" />
                ) : (
                  <BellOff className="w-4 h-4 text-gray-500" />
                )}
              </div>
              <div>
                <p className="text-white text-sm font-medium">Push Notifications</p>
                <p className="text-xs text-gray-500">
                  {hasPermission ? 'Allowed' : 'Click to enable'}
                </p>
              </div>
            </div>
            <Switch
              checked={notificationsEnabled && hasPermission}
              onCheckedChange={toggleNotifications}
            />
          </div>

          {/* Sound */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${soundEnabled ? 'bg-green-500/20' : 'bg-[#252540]'}`}>
                {soundEnabled ? (
                  <Volume2 className="w-4 h-4 text-green-500" />
                ) : (
                  <VolumeX className="w-4 h-4 text-gray-500" />
                )}
              </div>
              <div>
                <p className="text-white text-sm font-medium">Sound Alert</p>
                <p className="text-xs text-gray-500">Play sound for new emails</p>
              </div>
            </div>
            <Switch
              checked={soundEnabled}
              onCheckedChange={toggleSound}
            />
          </div>

          {/* Permission Status */}
          {!hasPermission && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-xs text-amber-400">
                <strong>Note:</strong> Browser notifications require your permission. 
                Enable notifications above to receive alerts.
              </p>
            </div>
          )}

          {/* Test Button */}
          <Button
            onClick={testNotification}
            variant="outline"
            size="sm"
            className="w-full border-[#2d2d44] text-gray-300 hover:bg-[#252540]"
          >
            <Bell className="w-4 h-4 mr-2" />
            Test Notification
          </Button>
        </div>

        {/* Status Footer */}
        <div className="p-3 bg-[#252540] border-t border-[#2d2d44] flex items-center justify-between">
          <span className="text-xs text-gray-500">Status:</span>
          <div className="flex items-center gap-1">
            {notificationsEnabled && hasPermission ? (
              <>
                <Check className="w-3 h-3 text-green-500" />
                <span className="text-xs text-green-500">Active</span>
              </>
            ) : (
              <>
                <X className="w-3 h-3 text-gray-500" />
                <span className="text-xs text-gray-500">Inactive</span>
              </>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
